import { Popover, PopoverBackdrop, PopoverButton, PopoverPanel } from '@headlessui/react';
import Link from 'next/link';
import type React from 'react';
import { useMemo } from 'react';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { CinemaChainDto } from '@cineamo/api/models/cinema-chain/CinemaChainDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { DROPDOWN_DIVIDER_CLASSNAME, DROPDOWN_MENU_ITEM_CLASSNAME } from '@cineamo/ui/src/dropdown/dropdown-colors';
import DropdownPanel from '@cineamo/ui/src/dropdown/DropdownPanel';
import { IconDown16 } from '@cineamo/ui/src/icons/ic-down';
import { IconLocation16 } from '@cineamo/ui/src/icons/ic-location';
import { IconUp16 } from '@cineamo/ui/src/icons/ic-up';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const PartnerLinksDropdown: React.FC<{
    cinemaChain: CinemaChainDto | null;
    cinemasInChain: CinemaDto[];
}> = ({ cinemaChain, cinemasInChain }) => {
    const domainConfiguration = useDomainConfiguration();
    const selectedCinema = domainConfiguration.cinema;
    const partnerLinksDropdownConfiguration = domainConfiguration.websiteConfiguration.partnerLinksDropdown ?? {};

    const cinemasLinks = useMemo(
        () =>
            cinemasInChain
                .map((cinema) => ({
                    ...cinema,
                    domain: cinema._embedded?.cinemaConfiguration?.domain
                }))
                .filter(
                    ({ domain, id }) => domain && !partnerLinksDropdownConfiguration?.excludedCinemaIds?.includes(id)
                ),
        [cinemasInChain, partnerLinksDropdownConfiguration.excludedCinemaIds]
    );

    const additionalLinks = partnerLinksDropdownConfiguration?.additionalLinks ?? [];

    // The panel and its rows read the same two variables every dropdown in `packages/ui` uses, so
    // this stays in step with them instead of restating the palette locally.
    //
    // The hover is unchanged: `--surface-tint-color` resolves to exactly the
    // `withOpacity(primaryColor, 0.2)` this used to compute by hand on a themed domain. The fills
    // do shift slightly — rows move from `--surface-color` to the non-transparent blend of that
    // same colour over the page background, which is what they already rendered as, and the panel
    // moves from the raw `--background-color` to that blend, so it now matches its rows rather than
    // showing through between them.
    const linkClass = cn('py-12 lg:py-8 px-16 block text-body-14 leading-[24px]', DROPDOWN_MENU_ITEM_CLASSNAME);

    const cinemaChainLabel = partnerLinksDropdownConfiguration?.cinemaChainLabel ?? cinemaChain?.name;

    return (
        <Popover className="relative cursor-pointer whitespace-nowrap lg:ml-[40px]">
            {({ open }) => (
                <>
                    <PopoverButton
                        className={cn(
                            'w-full max-w-[240px] flex items-center space-x-8 justify-between p-12',
                            // On the page background, so `surface` rather than the tint — the same
                            // call the footer language selector makes.
                            'bg-[var(--non-transparent-surface-color)] text-[var(--on-non-transparent-surface-color)]',
                            'border border-[var(--surface-tint-color)]',
                            'rounded-4'
                        )}>
                        <IconLocation16 className="shrink-0" />
                        <span className="hidden lg:block text-body-bold-14 truncate">{selectedCinema.name}</span>
                        {open ? <IconUp16 className="shrink-0" /> : <IconDown16 className="shrink-0" />}
                    </PopoverButton>
                    <PopoverBackdrop className="fixed inset-0 w-full h-full bg-black/80 lg:hidden z-[60]" />
                    <DropdownPanel
                        as={PopoverPanel}
                        anchor="top start"
                        className={cn(
                            'rounded-8 [--anchor-gap:10px] z-[60]',
                            // The group separators share the panel's own hairline, instead of a
                            // `white/40` that only read correctly on a dark domain.
                            'divide-y',
                            DROPDOWN_DIVIDER_CLASSNAME
                        )}>
                        {cinemasLinks.length > 0 && (
                            <div>
                                {cinemasLinks.map((cinema) => (
                                    <Link
                                        key={cinema.id}
                                        href={`https://${cinema.domain}`}
                                        className={cn(
                                            linkClass,
                                            selectedCinema.id === cinema.id && 'text-body-bold-14'
                                        )}>
                                        {cinema.name}
                                    </Link>
                                ))}
                            </div>
                        )}
                        {additionalLinks.length > 0 && (
                            <div>
                                {additionalLinks.map((link) => (
                                    <Link
                                        key={link.url}
                                        href={link.url}
                                        className={cn(linkClass)}>
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                        {partnerLinksDropdownConfiguration?.cinemaChainUrl && cinemaChainLabel && (
                            <div>
                                <Link
                                    href={partnerLinksDropdownConfiguration.cinemaChainUrl}
                                    className={cn(linkClass)}>
                                    {cinemaChainLabel}
                                </Link>
                            </div>
                        )}
                    </DropdownPanel>
                </>
            )}
        </Popover>
    );
};

export default PartnerLinksDropdown;
